import { Session } from "@supabase/supabase-js";
import React, { useState, useEffect, useContext } from "react";

// Define the type for FeatureFlag data
export interface FeatureFlagData {
  isEnabled: boolean;
  trialEndDate: Date | null;
}

export type FeatureFlags = Map<string, FeatureFlagData>;

// Define the context value type
interface FeatureFlagsContextValue {
  featureFlags: Map<string, FeatureFlagData>;
  isLoading: boolean;
  firmId: string | null | undefined;
  userId: string | undefined;
}

// Create the FeatureFlagsContext
const FeatureFlagsContext =
  React.createContext<FeatureFlagsContextValue | null>(null);

// Custom hook to use the feature flags context
export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    return {
      featureFlags: new Map(),
      isLoading: true,
      userId: undefined,
      firmId: null,
    };
  }
  return context;
};

// Hook to check if a feature is enabled
export const useIsFeatureEnabled = (featureName: string): boolean | null => {
  const { featureFlags, isLoading } = useFeatureFlags();

  if (isLoading) {
    // During loading, return null to indicate that the feature flags are not yet loaded
    return null;
  }

  const featureFlag = featureFlags.get(featureName);
  return featureFlag ? featureFlag.isEnabled : false;
};

// FeatureFlagProvider props
interface FeatureFlagProviderProps {
  service: FeatureFlagService | null;
  children: React.ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  service,
  children,
}) => {
  const [featureFlags, setFeatureFlags] = useState(
    new Map<string, FeatureFlagData>(),
  );
  const [isLoading, setIsLoading] = useState(
    service ? service.getIsLoading() : true,
  );
  const [timedOut, setTimedOut] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!service) {
      return;
    }

    const unsubscribeFeatureFlags = service.subscribeToFeatureFlagChanges(
      (updatedFeatureFlags) => {
        setFeatureFlags(new Map(updatedFeatureFlags));
      },
    );

    const unsubscribeIsLoading =
      service.subscribeToIsLoadingChanges(setIsLoading);

    return () => {
      unsubscribeFeatureFlags();
      unsubscribeIsLoading();
    };
  }, [service]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setTimedOut(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Give up completely after 20 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setGaveUp(true);
      }
    }, 20000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // If loading completes after timeout, hide the timeout message
  useEffect(() => {
    if (!isLoading && timedOut && !gaveUp) {
      setTimedOut(false);
    }
  }, [isLoading, timedOut, gaveUp]);

  if (isLoading || !service) {
    if (gaveUp) {
      return (
        <div className="flex h-screen w-full items-center justify-center">
          <div className="max-w-md bg-zinc-100 p-6 rounded-lg">
            <p className="text-zinc-800 font-medium text-lg">
              Unable to connect to the server. Please check your internet
              connection and try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 w-full bg-zinc-800 text-white py-2 px-4 rounded hover:bg-zinc-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    if (timedOut) {
      return (
        <div className="flex h-screen w-full items-center justify-center">
          <div className="max-w-md bg-zinc-100 p-6 rounded-lg">
            <div className="text-zinc-800 font-medium text-lg space-y-3">
              <p>Hmm, this is taking longer than expected.</p>
              <div className="text-base space-y-1">
                <p>
                  • Check your internet connection and our{" "}
                  <a
                    href="https://status.briefly.legal/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    status page
                  </a>
                </p>
                <p>• If using a VPN, try disabling it and refreshing</p>
                <p>
                  • If on an office network, try a different network or contact
                  your IT team about access
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col items-center gap-2">
              <span className="loading loading-spinner h-8 w-8" />
              <p className="text-sm text-zinc-600">
                Still trying to connect...
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen w-full items-center justify-center text-gray-800">
        <span className="loading loading-spinner h-10 w-10" />
      </div>
    );
  }

  return (
    <FeatureFlagsContext.Provider
      value={{
        featureFlags,
        isLoading,
        firmId: service.getFirmId(),
        userId: service.getUserId(),
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

// FeatureFlagService interface
export interface FeatureFlagService {
  loadInitialFeatureFlags: () => Promise<void>;
  getAllFeatureFlags: () => Map<string, FeatureFlagData>;
  subscribeToFeatureFlagChanges: (
    callback: (featureFlags: Map<string, FeatureFlagData>) => void,
  ) => () => void;
  subscribeToIsLoadingChanges(
    callback: (newState: boolean) => void,
  ): () => void;
  getIsLoading(): FeatureFlagsContextValue["isLoading"];
  getUserId(): FeatureFlagsContextValue["userId"];
  getFirmId(): FeatureFlagsContextValue["firmId"];
}

// BaseFeatureFlagService class with getIdentifier method
export abstract class BaseFeatureFlagService implements FeatureFlagService {
  protected userId?: string;
  protected firmId?: string | null;

  constructor(config: { userId?: string; firmId?: string | null }) {
    this.userId = config.userId;
    this.firmId = config.firmId;
  }

  // Reintroduce the getIdentifier method
  protected getIdentifier(options?: { userId?: string; firmId?: string }) {
    return {
      userId: options?.userId ?? this.userId,
      firmId: options?.firmId ?? this.firmId,
    };
  }

  abstract loadInitialFeatureFlags(): Promise<void>;
  abstract getAllFeatureFlags(): Map<string, FeatureFlagData>;
  abstract subscribeToFeatureFlagChanges(
    callback: (featureFlags: Map<string, FeatureFlagData>) => void,
  ): () => void;
  abstract subscribeToIsLoadingChanges(
    callback: (newState: boolean) => void,
  ): () => void;
  abstract getIsLoading(): FeatureFlagsContextValue["isLoading"];
  abstract getUserId(): FeatureFlagsContextValue["userId"];
  abstract getFirmId(): FeatureFlagsContextValue["firmId"];
}
